import { schoolPrices } from "@/config/schoolPrices";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Navigation } from "./Index/components/Navigation";
import { Footer } from "./Index/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { Button } from "@/components/ui/button";
import { MessageCircle, Bus, Clock, Utensils, Blocks } from "lucide-react";
import { OpenHouseBanner } from "@/components/OpenHouseBanner";
import { motion } from "framer-motion";
import { openWhatsApp, WHATSAPP_MESSAGES } from "@/lib/whatsapp";
import { usePageMeta } from "@/hooks/usePageMeta";

const META: Record<string, { title: string; description: string }> = {
  es: {
    title: "Servicios complementarios | Gándara International School",
    description: "Transporte escolar, comedor, aula madruga y más servicios complementarios en Gándara International School, Gondomar.",
  },
  en: {
    title: "Additional Services | Gándara International School",
    description: "School transport, dining, early morning care and more additional services at Gándara International School, Gondomar.",
  },
  pt: {
    title: "Serviços Complementares | Gándara International School",
    description: "Transporte escolar, cantina, acolhimento matinal e mais serviços complementares na Gándara International School, Gondomar.",
  },
  fr: {
    title: "Services Complémentaires | Gándara International School",
    description: "Transport scolaire, cantine, accueil matinal et autres services complémentaires à Gándara International School, Gondomar.",
  },
};

const EXTRA_PATH: Record<string, string> = { es: "extraescolares", en: "extracurriculars", pt: "extracurriculares", fr: "extrascolaires" };

const Services = () => {
  const { language } = useLanguage();
  const t = translations[language].services;
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const meta = META[language] || META.es;
  usePageMeta({ title: meta.title, description: meta.description });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <OpenHouseBanner onVisibilityChange={setIsBannerVisible} />
      
      <div className={`${isBannerVisible ? 'pt-[120px]' : 'pt-20'} pb-20`}>
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-6xl mx-auto"
          >
            <div className="text-center mb-16">
              <h1 className="text-5xl font-light text-primary mb-6">
                {t.title}
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {t.subtitle}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="bg-card rounded-2xl p-8 shadow-lg"
              >
                <Bus className="w-12 h-12 text-primary mb-4" />
                <h2 className="text-2xl font-light text-primary mb-4">
                  {t.transport.title}
                </h2>
                <p className="text-muted-foreground mb-4">
                  {t.transport.description}
                </p>
                <ul className="space-y-2">
                  <li className="flex justify-between items-center">
                    <span className="text-muted-foreground">{t.transport.roundTrip}</span>
                    <span className="font-medium text-primary">{t.transport.roundTripPrice}</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="bg-card rounded-2xl p-8 shadow-lg"
              >
                <Clock className="w-12 h-12 text-primary mb-4" />
                <h2 className="text-2xl font-light text-primary mb-4">
                  {t.earlyBirds.title}
                </h2>
                <p className="text-muted-foreground mb-4">
                  {t.earlyBirds.schedule}
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex justify-between items-center">
                    <span className="text-muted-foreground">{t.earlyBirds.monthly}</span>
                    <span className="font-medium text-primary">{t.earlyBirds.monthlyPrice}</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-muted-foreground">{t.earlyBirds.daily}</span>
                    <span className="font-medium text-primary">{t.earlyBirds.dailyPrice}</span>
                  </li>
                </ul>
                <p className="text-sm text-muted-foreground">
                  {t.earlyBirds.note}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
                className="bg-card rounded-2xl p-8 shadow-lg"
              >
                <Utensils className="w-12 h-12 text-primary mb-4" />
                <h2 className="text-2xl font-light text-primary mb-4">
                  {t.lunch.title}
                </h2>
                <p className="text-muted-foreground mb-4">
                  {t.lunch.description}
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex justify-between items-center">
                    <span className="text-muted-foreground">{t.lunch.monthly}</span>
                    <span className="font-medium text-primary">{schoolPrices.additionalServices.food.monthly}{t.lunch.perMonth}</span>
                  </li>
                  <li className="flex justify-between items-center gap-3">
                    <span className="text-muted-foreground">{t.lunch.threeDays}</span>
                    <span className="font-medium text-primary whitespace-nowrap">{schoolPrices.additionalServices.food.threeDays}{t.lunch.perMonth}</span>
                  </li>
                  <li className="flex justify-between items-center gap-3">
                    <span className="text-muted-foreground">{t.lunch.twoDays}</span>
                    <span className="font-medium text-primary whitespace-nowrap">{schoolPrices.additionalServices.food.twoDays}{t.lunch.perMonth}</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-muted-foreground">{t.lunch.daily}</span>
                    <span className="font-medium text-primary">{schoolPrices.additionalServices.food.daily}</span>
                  </li>
                </ul>
                <p className="text-sm text-muted-foreground mb-4">{t.lunch.note}</p>
                <div className="bg-primary/5 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-primary mb-2">
                    {t.lunch.takeaway.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t.lunch.takeaway.description}
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
                className="bg-card rounded-2xl p-8 shadow-lg"
              >
                <Blocks className="w-12 h-12 text-primary mb-4" />
                <h2 className="text-2xl font-light text-primary mb-4">
                  {t.ludoteca.title}
                </h2>
                <p className="text-muted-foreground mb-4">
                  {t.ludoteca.schedule}
                </p>
                <p className="text-muted-foreground mb-4">
                  {t.ludoteca.description}
                </p>
                <Link to={`/${language}/${EXTRA_PATH[language] || "extraescolares"}`} className="block bg-accent/10 hover:bg-accent/20 transition-colors rounded-lg p-3 mb-4">
                  <p className="text-sm font-medium text-primary">
                    {t.ludoteca.freeWith} →
                  </p>
                </Link>
                <ul className="space-y-2 mb-4">
                  {t.ludoteca.options.map((option, index: number) => (
                    <li key={index} className="flex justify-between items-center">
                      <span className="text-muted-foreground">{option.label}</span>
                      <span className="font-medium text-primary">{option.price}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-muted-foreground">
                  {t.ludoteca.note}
                </p>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8 text-center"
            >
              <h2 className="text-2xl font-light text-primary mb-4">
                {t.contact.title}
              </h2>
              <p className="text-muted-foreground mb-6">
                {t.contact.description}
              </p>
              <Button
                onClick={() => openWhatsApp(WHATSAPP_MESSAGES.bookVisit[language])}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 rounded-lg text-lg shadow-lg transform transition-all duration-200 hover:scale-105 hover:shadow-xl flex items-center gap-3 mx-auto w-fit"
              >
                <MessageCircle className="w-5 h-5" />
                {t.contact.button}
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Services;
